/** @jsxImportSource @emotion/react */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Titulo, tituloBase, useAcceso } from "../../shared";

// Un item con `permiso` solo lo ve quien tiene al menos lectura en ese
// permiso; sin `permiso`, lo ven todos.
const vinculos = [
  {
    titulo: "Comunicaciones y Reportes",
    items: [
      { nombre: "Eventos", icono: "pi pi-bell", uri: "/comunicacion/configeventos" },
      { nombre: "Juno", icono: "pi pi-microchip-ai", uri: "/juno/config" },
      { nombre: "Reportes", icono: "pi pi-chart-bar", uri: "/reporte" }
    ]
  },
  {
    titulo: "Funcionamiento general",
    items: [
      { nombre: "Normas", icono: "pi pi-book", uri: "/norma" },
      { nombre: "Esquemas", icono: "pi pi-th-large", uri: "/esquemas" },
      { nombre: "Tipos de Certificado", icono: "pi pi-file", uri: "/certificadotipos" },
      { nombre: "Tipos de Proceso", icono: "pi pi-sitemap", uri: "/procesotipos" },
      { nombre: "Numeración", icono: "pi pi-hashtag", uri: "/numeracion" },
      { nombre: "Contadores", icono: "pi pi-sort-numeric-up", uri: "/contadores" },
      { nombre: "Templates", icono: "pi pi-copy", uri: "/template" },
      { nombre: "Actividades", icono: "pi pi-list-check", uri: "/actividades/tipos" },
      { nombre: "Planes de Actividades", icono: "pi pi-calendar", uri: "/config/planes-actividades" },
      { nombre: "Estados de Certificado", icono: "pi pi-tags", uri: "/estadocertificado" },
      { nombre: "Tipos de Hallazgo", icono: "pi pi-flag", uri: "/hallazgotipo" },
      { nombre: "Tipos de Sitio", icono: "pi pi-map-marker", uri: "/sitiotipos" },
      { nombre: "Tipos de Colaborador", icono: "pi pi-id-card", uri: "/proveedores/auditortipos" },
      { nombre: "Carpetas de Documentos", icono: "pi pi-folder", uri: "/documentocarpetas" },
      { nombre: "Categorías de archivos", icono: "pi pi-bookmark", uri: "/config/categoriasarchivos" },
      { nombre: "Acciones para clientes", icono: "pi pi-bolt", uri: "/config/accionescliente" }
    ]
  },
  {
    titulo: "Configuración General",
    items: [
      { nombre: "Configuración General", icono: "pi pi-cog", uri: "/config/general" },
      {
        nombre: "Administradores",
        icono: "pi pi-users",
        uri: "/config/administradores",
        permiso: "admin.administradores"
      },
      { nombre: "Integraciones", icono: "pi pi-link", uri: "/config/integraciones" },
      { nombre: "Acuerdos de servicio", icono: "pi pi-file-check", uri: "/eula" }
    ]
  }
];

export default function ConfigPortada() {
  const navigate = useNavigate();
  const { permisos } = useAcceso();

  useEffect(() => {
    document.title = "Configuración";
    return () => (document.title = tituloBase());
  }, []);

  return (
    <>
      <Titulo urlVolver="/">Configuración</Titulo>

      <div css={{ display: "flex", flexDirection: "column", gap: "1em" }}>
        {vinculos.map((seccion) => (
          <Card key={seccion.titulo}>
            <Titulo>{seccion.titulo}</Titulo>
            <div
              css={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: "1em"
              }}
            >
              {seccion.items
                .filter(
                  (item) => !item.permiso || (permisos[item.permiso] ?? 0) >= 1
                )
                .map((item) => (
                <Card
                  key={item.uri}
                  css={{
                    cursor: "pointer",
                    // El borde va desde el principio en transparente: si
                    // apareciera recién en el hover, la tarjeta crecería 2px y
                    // el grid entero se movería al pasar el mouse.
                    "&.p-card": { border: "1px solid transparent" },
                    transition:
                      "border-color 0.2s, background-color 0.2s, box-shadow 0.2s",
                    // Antes el hover solo abría la sombra (blur 8 al 15%), que
                    // se dispersa y se lee más débil que la sombra de base: la
                    // tarjeta apuntada parecía la menos marcada. Ahora se marca
                    // con el color del organismo, como los inputs al enfocarse.
                    "&:hover": {
                      borderColor: "var(--principal)",
                      backgroundColor: "var(--principalLight)",
                      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.2)"
                    },
                    "&:hover i": { color: "var(--principal)" },
                    // El grid ya empareja el alto dentro de cada renglón; esto
                    // hace que la tarjeta ocupe todo lo que le da esa fila en
                    // vez de dejar la caja interna corta.
                    height: "100%",
                    "& .p-card-body, & .p-card-content": { height: "100%" }
                  }}
                  onClick={(e) => e.metaKey || e.ctrlKey ? window.open(item.uri, "_blank") : navigate(item.uri)}
                >
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      gap: "0.5em",
                      textAlign: "center"
                    }}
                  >
                    <i
                      className={item.icono}
                      css={{ fontSize: "1.5rem", color: "black" }}
                    />
                    {/* Se reservan siempre dos renglones. Sin esto, un renglón
                        con "Planes de Actividades" queda más alto que uno donde
                        todos los nombres entran en una línea. */}
                    <span
                      css={{
                        fontSize: "0.9rem",
                        fontWeight: 500,
                        lineHeight: 1.2,
                        minHeight: "2.4em",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      {item.nombre}
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}
